<template>
  <v-row dense>
    <v-row class="my-10" dense>
      <v-col
        v-for="(item, index) in visibleAnalytics"
        :key="index"
        cols="12"
        lg="3"
        sm="6"
      >
        <analytics-card
          :body="item.body"
          :color="item.color"
          :loading="loadingAnalytics"
          :title="item.title"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="9">
        <search-table
          :placeholder="tableSearchPlaceholderText"
          :text="resourceData.params.search"
          @change="
            isViewingStudent
              ? (studentData.params.search = $event)
              : (professorData.params.search = $event)
          "
          @clear="clearSearch"
          @submit="
            isViewingStudent ? getStudentList(true) : getProfessorList(true)
          "
        />
      </v-col>
      <v-col cols="12" md="3">
        <base-select
          v-model="viewType"
          item-text="text"
          item-value="value"
          :items="viewTypeDropdown"
          outlined
        />
      </v-col>

      <v-col cols="0" md="9" />
      <v-col v-if="isViewingStudent" cols="12" md="3">
        <base-autocomplete
          v-model="batch"
          clearable
          item-text="name"
          item-value="uuid"
          :items="batchChoices"
          :loading="batchLoading"
          outlined
          placeholder="Filter by Batch"
        />
      </v-col>

      <v-col cols="12">
        <base-data-table
          class="mb-10"
          gap="mid"
          :headers="headers"
          :items="resourceData.items"
          :items-per-page="resourceData.params.itemsPerPage"
          :loading="
            (isViewingStudent && studentLoading) ||
              (!isViewingStudent && professorLoading)
          "
          :page="resourceData.params.page"
        >
          <template v-slot:[`header.action`]>
            <total-entries-count
              :items-per-page="resourceData.params.itemsPerPage"
              :page="resourceData.params.page"
              :total="resourceData.total"
            />
          </template>

          <template v-slot:[`item.full_name`]="{ item }">
            <div v-if="isViewingStudent" class="d-flex py-0 py-md-5">
              <base-avatar :name="item.full_name" :photo="item.photo" />
              <div class="ml-3">
                <p>{{ item.full_name }}</p>
                <p class="text-caption text--secondary ">
                  {{ item.batch.name }}
                </p>
              </div>
            </div>
            <div v-else class="d-flex py-0 py-md-5">
              <p>{{ item.full_name }}</p>
            </div>
          </template>

          <template v-slot:[`item.evaluations`]="{ item }">
            <div class="d-flex align-center">
              <v-progress-circular
                color="primary"
                rotate="-90"
                :value="calculateProgressValue(item)"
              />
              <p class="ml-3" v-text="calculateProgressText(item)" />
            </div>
          </template>

          <template v-slot:[`item.challenges`]="{ item }">
            <p v-if="isViewingStudent && item.challenged_reviews">
              {{
                `${item.challenged_reviews} ${
                  item.challenged_reviews > 1 ? 'challenges' : 'challenge'
                }`
              }}
            </p>
            <p v-else-if="!isViewingStudent && item.challenged_evaluations">
              {{
                `${item.challenged_evaluations} ${
                  item.challenged_evaluations > 1 ? 'challenges' : 'challenge'
                }`
              }}
            </p>
            <p v-else>
              -
            </p>
          </template>

          <template v-slot:[`item.action`]="{ item }">
            <v-btn
              color="primary"
              link
              small
              text
              :to="constructRouterObject(item.uuid)"
            >
              View Assignments
            </v-btn>
          </template>
          <template v-slot:footer>
            <base-paginator
              :items-per-page="resourceData.params.itemsPerPage"
              :page="resourceData.params.page"
              :total="resourceData.total"
              @change="
                isViewingStudent
                  ? (studentData.params.page = $event)
                  : (professorData.params.page = $event)
              "
            />
          </template>
        </base-data-table>
      </v-col>
    </v-row>
  </v-row>
</template>

<script>
import AnalyticsCard from '@/components/_shared/AnalyticsCard'
import SearchTable from '@/components/_shared/SearchTable'
import TotalEntriesCount from '@/components/_shared/TotalEntriesCount'

import { mutationTypes } from '@/store/mutations'
import { EVALUATION_VIEW_TYPE_CHOICES } from '@/utils/constants'

export default {
  name: 'Evaluations',

  components: {
    AnalyticsCard,
    SearchTable,
    TotalEntriesCount,
  },

  data: () => ({
    headers: [
      {
        text: 'Name',
        sortable: false,
        value: 'full_name',
      },
      {
        text: 'Assignments Evaluated',
        sortable: false,
        value: 'evaluations',
      },
      {
        text: 'Evaluations Challenged',
        sortable: false,
        value: 'challenges',
      },
      {
        text: '',
        sortable: false,
        value: 'action',
        align: 'end',
      },
    ],

    studentData: {
      items: [],
      total: 0,
      params: {
        page: 1,
        itemsPerPage: 10,
        search: '',
      },
    },

    professorData: {
      items: [],
      total: 0,
      params: {
        page: 1,
        itemsPerPage: 10,
        search: '',
      },
    },

    studentLoading: false,
    professorLoading: false,
    loadingAnalytics: false,

    viewTypeDropdown: [
      { text: 'By Student', value: EVALUATION_VIEW_TYPE_CHOICES.BY_STUDENT },
      {
        text: 'By Professor',
        value: EVALUATION_VIEW_TYPE_CHOICES.BY_PROFESSOR,
      },
    ],

    batch: null,
    batchChoices: [],
    batchLoading: false,

    analytics: [],
  }),

  computed: {
    viewType: {
      get() {
        return this.$store.state.evaluationViewType
      },
      set(value) {
        this.$store.commit(mutationTypes.UPDATE_EVALUATION_VIEW_TYPE, value)
      },
    },

    tableSearchPlaceholderText() {
      if (this.viewType === EVALUATION_VIEW_TYPE_CHOICES.BY_STUDENT)
        return 'Search Student'
      else return 'Search Professor'
    },

    isViewingStudent() {
      return this.viewType === EVALUATION_VIEW_TYPE_CHOICES.BY_STUDENT
    },

    resourceData() {
      return this.isViewingStudent ? this.studentData : this.professorData
    },

    visibleAnalytics() {
      if (this.analytics.length === 0) {
        const dummyObject = {
          body: 'random',
          color: 'purple',
          title: 'Users Allocated',
        }
        // Need 4 dummy loading cards
        return new Array(4).fill(dummyObject)
      }

      if (this.isViewingStudent) {
        // Remove the analytic which is related to professor.
        return this.analytics.filter((_, i) => i !== 1)
      }

      // Remove the analytic which is related to student.
      return this.analytics.filter((_, i) => i !== 0)
    },
  },

  watch: {
    batch() {
      this.isViewingStudent
        ? this.getStudentList(true)
        : this.getProfessorList(true)
    },
    'studentData.params.page'() {
      this.getStudentList()
    },
    'professorData.params.page'() {
      this.getProfessorList()
    },
  },

  mounted() {
    this.getProfessorList()
    this.getStudentList()
    this.getAnalytics()
    this.getBatches()
  },

  methods: {
    calculateProgressValue({
      evaluated_assignments,
      total_assignments,
      evaluated_reviews,
      total_reviews,
    }) {
      const value = this.isViewingStudent
        ? evaluated_assignments
        : evaluated_reviews
      const totalValue = this.isViewingStudent
        ? total_assignments
        : total_reviews

      // 4 is just to show a little bit of progress in the ring. 0 looks very empty
      if (value / totalValue === 0) return 4
      else return (value / totalValue) * 100
    },

    calculateProgressText({
      evaluated_assignments,
      total_assignments,
      evaluated_reviews,
      total_reviews,
    }) {
      return this.isViewingStudent
        ? `${evaluated_assignments}/${total_assignments}`
        : `${((evaluated_reviews / total_reviews) * 100).toFixed(0)}%`
    },

    constructRouterObject(id) {
      if (this.isViewingStudent) {
        return {
          name: 'UserAssignments',
          params: { userId: id },
        }
      } else {
        return {
          name: 'ProfessorAssignments',
          params: { professorId: id },
        }
      }
    },

    async getAnalytics() {
      this.loadingAnalytics = true
      try {
        const res = await this.$api.assignments.getAnalytics()
        const analytics = res.data.data
        this.analytics = [
          {
            body: `${analytics.user_allocated}`,
            color: 'purple',
            title: 'Users Allocated',
          },
          {
            body: `${analytics.total_evaluations}`,
            color: 'purple',
            title: 'Total Evalutions',
          },
          {
            body: `${analytics.evaluations_completed}/${analytics.total_evaluations}`,
            color: 'green',
            title: 'Evaluations completed',
          },
          {
            body: `${analytics.pending_evaluations}/${analytics.total_evaluations}`,
            color: 'yellow',
            title: 'Pending Evaluations',
          },
          {
            body: `${analytics.challenged_evaluations}/${analytics.total_evaluations}`,
            color: 'red',
            title: 'Challenged Evaluations',
          },
        ]
        this.loadingAnalytics = false
      } catch (err) {
        console.error(err)
      }
    },

    clearSearch() {
      if (this.isViewingStudent) {
        this.studentData.params.search = ''
        this.getStudentList()
      } else {
        this.professorData.params.search = ''
        this.getProfessorList()
      }
    },

    async getProfessorList(searching = false) {
      this.professorLoading = true

      const { search, page } = this.professorData.params
      const params = {
        params: {
          search: search ?? '',
          page,
        },
      }
      if (this.batch) params.params.batch = this.batch
      if (searching) params.params.page = 1

      try {
        const res = await this.$api.users.listProfessors(params)
        this.professorData.items = res.data.data
        this.professorData.total = res.data.total
      } catch (err) {
        console.error(err)
      } finally {
        this.professorLoading = false
      }
    },

    async getStudentList(searching = false) {
      this.studentLoading = true

      const { search, page } = this.studentData.params
      const params = {
        params: {
          search: search ?? '',
          page,
        },
      }
      if (this.batch) params.params.batch = this.batch
      if (searching) params.params.page = 1

      try {
        const res = await this.$api.users.listStudents(params)
        this.studentData.items = res.data.data
        this.studentData.total = res.data.total
      } catch (err) {
        console.error(err)
      } finally {
        this.studentLoading = false
      }
    },

    async getBatches() {
      this.batchLoading = true
      try {
        const res = await this.$api.batches.getBatches({
          params: {
            page: 'all',
          },
        })
        this.batchChoices = res.data.data
      } catch (err) {
        console.error(err)
      } finally {
        this.batchLoading = false
      }
    },
  },
}
</script>
